import { CharacterData } from '../types/character';
// Import equipment resolver functions
import { 
  resolveEquipmentReference, 
  resolveEquipmentReferences,
  ResolvedEquipmentItem,
  ResolvedEquipmentWithCharacterData,
  getEquippedItems,
  getInventoryDisplayItems
} from './equipmentResolver';

// Universal Content Reference Interface
export interface ContentReference {
  contentId: string;        // Reference to Content Library item ID
  contentType: 'equipment' | 'domain_card';
  quantity?: number;        // For equipment (default: 1 for domain cards)
  equippedCount?: number;   // For equipment only
}

// Base resolved content interface
export interface ResolvedContent {
  id: string;
  name: string;
  type: string;
  category?: string;
  description?: string;
  modifiers?: any[];
  // Content type specific properties
  [key: string]: any;
}

// Domain card specific resolved interface
export interface ResolvedDomainCard extends ResolvedContent {
  type: 'passive' | 'active' | 'concentration';
  domain?: string;
  level?: number;
  originalType?: string; // Preserve the original type from domain card data
  // Domain card specific properties
}

// Resolved domain card with character-specific data
export interface ResolvedDomainCardWithCharacterData extends ResolvedDomainCard {
  quantity: number; // Always 1 for domain cards
  // Domain cards don't have equipped count - they're either owned or not
}

// Cache for resolved domain card data
const domainCardCache = new Map<string, ResolvedDomainCard | null>();
const domainCardCacheTimestamps = new Map<string, number>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Resolves a single domain card reference to its Content Library data
 */
export const resolveDomainCardReference = async (cardId: string): Promise<ResolvedDomainCard | null> => {
  // Check cache first
  const cached = getCachedDomainCard(cardId);
  if (cached !== undefined) {
    return cached;
  }

  try {
    // Import the domain card loading function
    const { findDomainCard } = await import('./contentLibraryHelpers');
    const domainCardData = await findDomainCard(cardId);
    
    if (!domainCardData) {
      // Cache null result to avoid repeated failed lookups
      setCachedDomainCard(cardId, null);
      return null;
    }

    // Transform to resolved format following the domain card to spell pattern
    const resolved: ResolvedDomainCard = {
      ...domainCardData,
      id: cardId,
      originalType: domainCardData.type, // Preserve original type from domain card
      type: determineDomainCardType(domainCardData),
      modifiers: enhanceDomainCardModifiers(domainCardData, cardId)
    };

    // Cache the result
    setCachedDomainCard(cardId, resolved);
    return resolved;
  } catch (error) {
    console.error(`Failed to resolve domain card reference: ${cardId}`, error);
    setCachedDomainCard(cardId, null);
    return null;
  }
};

/**
 * Resolves multiple domain card references in batch
 */
export const resolveDomainCardReferences = async (
  references: ContentReference[]
): Promise<(ResolvedDomainCardWithCharacterData | null)[]> => {
  const promises = references
    .filter(ref => ref.contentType === 'domain_card')
    .map(async (ref) => {
      const resolved = await resolveDomainCardReference(ref.contentId);
      if (!resolved) {
        return null;
      }

      return {
        ...resolved,
        quantity: ref.quantity || 1 // Domain cards always have quantity 1
      };
    });

  return Promise.all(promises);
};

/**
 * Universal content resolver dispatcher
 */
export const resolveContentReference = async (
  reference: ContentReference
): Promise<ResolvedContent | null> => {
  switch (reference.contentType) {
    case 'equipment':
      return await resolveEquipmentReference(reference.contentId);
    case 'domain_card':
      return await resolveDomainCardReference(reference.contentId);
    default:
      console.warn(`Unknown content type: ${reference.contentType}`);
      return null;
  }
};

/**
 * Resolves mixed content references (both equipment and domain cards)
 */
export const resolveContentReferences = async (
  references: ContentReference[]
): Promise<ResolvedContent[]> => {
  const promises = references.map(ref => resolveContentReference(ref));
  const resolved = await Promise.all(promises);
  return resolved.filter((item): item is ResolvedContent => item !== null);
};

/**
 * Gets all domain cards for a character (for stat computation)
 * NOTE: This returns all cards but CharacterStatComputer only processes loadout cards
 */
export const getDomainCards = async (character: CharacterData): Promise<ResolvedDomainCard[]> => {
  const allRefs = [
    ...(character.inventory?.domainCardLoadout || []),
    ...(character.inventory?.domainCardVault || [])
  ];
  
  if (allRefs.length === 0) {
    return [];
  }

  const resolvedCards = await resolveDomainCardReferences(allRefs);
  return resolvedCards.filter((card): card is ResolvedDomainCardWithCharacterData => card !== null);
};

/**
 * Gets all domain cards for UI display (resolved with character data)
 * Returns both loadout and vault cards for the unified display
 */
export const getDomainCardsForDisplay = async (character: CharacterData): Promise<ResolvedDomainCardWithCharacterData[]> => {
  const allRefs = [
    ...(character.inventory?.domainCardLoadout || []),
    ...(character.inventory?.domainCardVault || [])
  ];
  
  if (allRefs.length === 0) {
    return [];
  }

  const resolved = await resolveDomainCardReferences(allRefs);
  return resolved.filter((card): card is ResolvedDomainCardWithCharacterData => card !== null);
};

/**
 * Determines domain card type from domain card data (following existing pattern)
 */
const determineDomainCardType = (domainCardData: any): 'passive' | 'active' | 'concentration' => {
  // Map domain card types to character spell types (following existing transformDomainCardToSpell logic)
  const spellType = domainCardData.type === 'spell' ? 'active' : 
                   domainCardData.type === 'ability' ? 'passive' : 
                   'passive'; // Default to passive for other types

  return spellType as 'passive' | 'active' | 'concentration';
};

/**
 * Enhances domain card modifiers (following existing pattern but for domain cards)
 */
const enhanceDomainCardModifiers = (domainCardData: any, cardId: string): any[] => {
  // Return existing modifiers from the domain card data
  // Domain cards already have their modifiers properly processed by the existing system
  return domainCardData.modifiers || [];
};

/**
 * Domain card cache management functions
 */
const getCachedDomainCard = (cardId: string): ResolvedDomainCard | null | undefined => {
  const timestamp = domainCardCacheTimestamps.get(cardId);
  if (!timestamp || Date.now() - timestamp > CACHE_DURATION) {
    // Cache expired
    domainCardCache.delete(cardId);
    domainCardCacheTimestamps.delete(cardId);
    return undefined;
  }
  return domainCardCache.get(cardId);
};

const setCachedDomainCard = (cardId: string, domainCard: ResolvedDomainCard | null): void => {
  domainCardCache.set(cardId, domainCard);
  domainCardCacheTimestamps.set(cardId, Date.now());
};

/**
 * Clears the domain card cache (useful for testing or manual refresh)
 */
export const clearDomainCardCache = (): void => {
  domainCardCache.clear();
  domainCardCacheTimestamps.clear();
};

/**
 * Gets cache statistics for debugging
 */
export const getDomainCardCacheStats = (): { size: number; items: string[] } => {
  return {
    size: domainCardCache.size,
    items: Array.from(domainCardCache.keys())
  };
};

// Re-export equipment resolver functions for convenience
export {
  resolveEquipmentReference,
  resolveEquipmentReferences,
  getEquippedItems,
  getInventoryDisplayItems,
  type ResolvedEquipmentItem,
  type ResolvedEquipmentWithCharacterData
};